import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import WhatsAppButton from '@/components/feature/WhatsAppButton';
import useSeo from '@/hooks/useSeo';
import { ASSETS } from '@/constants/assets';

const problems = [
  'Perda de pressão em bombas de guindastes portuários',
  'Baixa vazão em sistemas de movimentação de cargas',
  'Ruídos excessivos em bombas de alta rotação em portos',
  'Superaquecimento por ciclos contínuos de carga e descarga',
  'Contaminação por poeira e partículas em mineração e logística',
  'Desgaste prematuro de componentes internos por ambiente marítimo',
];

const processSteps = [
  { title: 'Inspeção Técnica no Porto ou Planta', desc: 'Avaliação in loco da bomba em operação, mapeando condições de trabalho em portos, logística e mineração em Vitória.' },
  { title: 'Diagnóstico de Falhas', desc: 'Análise técnica detalhada considerando as demandas do complexo portuário, operações de carga e descarga e sistemas de mineração.' },
  { title: 'Desmontagem e Catalogação', desc: 'Desmontagem controlada com identificação e documentação de todos os componentes para rastreabilidade total.' },
  { title: 'Análise de Rotores e Eixos', desc: 'Verificação dimensional de rotores, eixos, pistões e engrenagens para avaliar recuperação ou substituição.' },
  { title: 'Recuperação de Componentes', desc: 'Retífica, usinagens especiais e tratamentos anticorrosivos de eixos e carcaças conforme especificação técnica.' },
  { title: 'Testes em Bancada', desc: 'Validação em bancada de testes com medição de pressão, vazão, ruído e funcionamento dinâmico em carga.' },
];

const segments = [
  'Portos',
  'Logística Portuária',
  'Mineração',
  'Operações de Carga e Descarga',
  'Transporte Marítimo',
  'Indústrias do Complexo Portuário',
];

const differentials = [
  'Atendimento rápido para Vitória e complexo portuário',
  'Recuperação de bombas de guindastes e equipamentos portuários',
  'Processos com rastreabilidade total e garantia técnica',
  'Logística otimizada para reduzir tempo de parada em operações críticas',
  'Diagnóstico realizado por técnicos com experiência em campo portuário',
  'Parceiros homologados para processos anticorrosivos e usinagens especiais',
];

const faqs = [
  { q: 'A Pilar atende diretamente em Vitória?', a: 'Sim, a Pilar realiza atendimento diretamente na região de Vitória, com coleta do equipamento, diagnóstico in loco e entrega após recuperação completa no complexo portuário.' },
  { q: 'Quais tipos de bombas são mais recuperadas em Vitória?', a: 'Bombas de pistão axial e radial, bombas de engrenagem e bombas de palhetas utilizadas em guindastes portuários, sistemas de movimentação de cargas, mineração e equipamentos do complexo portuário.' },
  { q: 'Qual o prazo médio de recuperação?', a: 'O prazo varia conforme o estado do equipamento e a necessidade de processos especializados. Após diagnóstico técnico, a Pilar apresenta cronograma realista focado na menor indisponibilidade.' },
];

export default function VitoriaBombasPage() {
  useSeo({
    title: 'Recuperação de Bombas Hidráulicas em Vitória | Oficina Pilar',
    description: 'Recuperação de bombas hidráulicas em Vitória e complexo portuário. Atendimento especializado para portos, logística, mineração e operações industriais do litoral capixaba.',
    keywords: 'recuperação de bombas hidráulicas Vitória, reparo de bombas Vitória, manutenção de bombas hidráulicas portos ES, bombas guindastes portuários Vitória, bombas mineração Vitória, Grupo Pilar Vitória',
    canonical: 'https://www.pilarmanutencao.com.br/recuperacao-bombas-hidraulicas-vitoria',
    ogUrl: 'https://www.pilarmanutencao.com.br/recuperacao-bombas-hidraulicas-vitoria',
    ogImage: ASSETS.oficina,
  });

  return (
    <div className="min-h-screen bg-gp-graphite">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative min-h-[60vh] flex items-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="/images/LOCAL/VITORIA/vitoria-bombas-hero.jpg"
              alt="Recuperação de bombas hidráulicas em Vitória - Grupo Pilar"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gp-graphite/75"></div>
          </div>
          <div className="relative z-10 w-full px-4 sm:px-6 lg:px-12 pt-28 pb-16">
            <div className="max-w-3xl">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-[2px] bg-gp-yellow"></div>
                <span className="text-gp-yellow text-sm uppercase tracking-[0.3em] font-medium">Vitória - Complexo Portuário</span>
              </div>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-white uppercase leading-tight mb-6">
                <span className="text-gp-yellow">Recuperação</span> de Bombas Hidráulicas em Vitória
              </h1>
              <p className="text-gray-300 text-lg md:text-xl max-w-2xl leading-relaxed">
                Serviço especializado de recuperação de bombas hidráulicas para portos, logística, mineração e operações industriais do complexo portuário de Vitória.
              </p>
            </div>
          </div>
        </section>

        {/* Introdução Local */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-8">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Atuação em Vitória e Complexo Portuário</h2>
              </div>
              <p className="text-gray-300 text-lg leading-relaxed mb-6">
                Vitória é o principal hub logístico e portuário do Espírito Santo, com operações intensivas de movimentação de cargas, mineração e transporte marítimo. As bombas hidráulicas operam em ambiente agressivo com alta frequência de uso. A Pilar oferece recuperação completa com foco em resistência, durabilidade e retorno rápido à operação.
              </p>
              <p className="text-gray-400 leading-relaxed">
                Recuperamos bombas de pistão, engrenagem e palhetas utilizadas em guindastes, sistemas de carga e descarga e equipamentos de mineração do complexo portuário.
              </p>
            </div>
          </div>
        </section>

        {/* Problemas */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Principais Problemas nas Indústrias de Vitória</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {problems.map((problem, index) => (
                <div key={index} className="flex items-center gap-4 bg-gp-graphite border border-white/10 rounded-lg px-5 py-4 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-close-circle-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{problem}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Processo */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Como a Pilar Executa o Serviço</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {processSteps.map((step, index) => (
                <div key={index} className="group bg-gp-graphite hover:bg-gp-graphite-lighter p-6 rounded-lg transition-all duration-300 hover:-translate-y-1 border border-transparent hover:border-gp-yellow/20">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center mb-4 group-hover:bg-gp-yellow transition-colors duration-300">
                    <span className="text-gp-yellow group-hover:text-gp-graphite font-display font-bold text-sm transition-colors duration-300">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                  </div>
                  <h3 className="font-display text-base font-bold text-white uppercase mb-2 group-hover:text-gp-yellow transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">{step.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Segmentos */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Segmentos Atendidos em Vitória</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {segments.map((segment, index) => (
                <div key={index} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-5 py-4 hover:bg-gp-yellow/10 hover:border-gp-yellow/30 transition-all duration-300">
                  <div className="w-10 h-10 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-building-2-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{segment}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Diferenciais */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex items-center gap-3 mb-10">
              <div className="w-10 h-[3px] bg-gp-yellow"></div>
              <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Diferenciais da Pilar para Vitória</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {differentials.map((diff, index) => (
                <div key={index} className="flex items-center gap-3 bg-gp-graphite border border-white/10 rounded-lg px-4 py-3 hover:border-gp-yellow/30 transition-colors duration-300">
                  <div className="w-8 h-8 bg-gp-yellow/10 rounded-sm flex items-center justify-center flex-shrink-0">
                    <i className="ri-check-line text-gp-yellow w-4 h-4 flex items-center justify-center"></i>
                  </div>
                  <span className="text-gray-300 text-sm font-medium">{diff}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="relative py-20 bg-gp-graphite-light">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="max-w-4xl mx-auto">
              <div className="flex items-center gap-3 mb-10">
                <div className="w-10 h-[3px] bg-gp-yellow"></div>
                <h2 className="text-white text-xl md:text-2xl font-bold uppercase tracking-wider">Perguntas Frequentes</h2>
              </div>
              <div className="space-y-4">
                {faqs.map((faq, index) => (
                  <div key={index} className="bg-gp-graphite border border-white/10 rounded-lg p-6">
                    <h3 className="font-display text-base font-bold text-white uppercase mb-3">{faq.q}</h3>
                    <p className="text-gray-400 text-sm leading-relaxed">{faq.a}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="relative py-20">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="relative rounded-xl overflow-hidden">
              <div className="absolute inset-0 bg-gradient-to-br from-gp-yellow/20 to-gp-graphite"></div>
              <div className="absolute inset-0 bg-gp-graphite/60"></div>
              <div className="relative z-10 p-8 md:p-12 text-center">
                <h2 className="font-display text-2xl md:text-3xl font-bold text-white uppercase mb-4">
                  Precisa recuperar uma bomba hidráulica em Vitória?
                </h2>
                <p className="text-gray-300 max-w-2xl mx-auto mb-8 leading-relaxed">
                  Solicite uma avaliação técnica e receba um diagnóstico especializado para recuperação do seu equipamento no complexo portuário de Vitória.
                </p>
                <a href="https://wa.me/5527993092922" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-gp-yellow hover:bg-gp-yellow-dark text-gp-graphite px-8 py-4 rounded-full font-semibold text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 whitespace-nowrap">
                  <div className="w-10 h-10 bg-gp-graphite rounded-full flex items-center justify-center">
                    <i className="ri-whatsapp-line text-gp-yellow w-5 h-5 flex items-center justify-center"></i>
                  </div>
                  <span>Solicitar Orçamento</span>
                  <i className="ri-arrow-right-up-line w-5 h-5 flex items-center justify-center"></i>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Links Internos */}
        <section className="py-10 border-t border-gp-graphite-lighter">
          <div className="px-4 sm:px-6 lg:px-12">
            <div className="flex flex-wrap items-center justify-center gap-6">
              <Link to="/recuperacao-bombas-hidraulicas" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-arrow-left-line w-5 h-5 flex items-center justify-center"></i>
                Página Principal do Serviço
              </Link>
              <Link to="/contato" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-mail-line w-5 h-5 flex items-center justify-center"></i>
                Contato
              </Link>
              <a href="https://wa.me/5527993092922" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 text-gray-400 hover:text-gp-yellow transition-colors duration-300 text-sm uppercase tracking-wider">
                <i className="ri-whatsapp-line w-5 h-5 flex items-center justify-center"></i>
                WhatsApp
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppButton />
    </div>
  );
}